import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  PrimaryButton,
  SingleSelect,
  Text,
  PhoneWithIsdInput,
} from "widgets";
import { useFetchCustomerInfo } from "store/backend/assets";
import { useCountriesMappings } from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { isValidPhoneForIsd } from "constants/phoneConfig";
import { ERROR_MESSAGES } from "constants/errorMessages";
import { getDefaultCountry } from "utils/countryMappings";
import {
  buildCustomerOrdersPath,
  isLearnOnlyRegion,
} from "constants/omsRegionConfig";

import {
  LEARN_VERTICAL_ID,
  BUSINESS_VERTICAL_IDS,
} from "constants/constants";
import Loader from "shared/Loader/Loader";
import MultipleUsers from "components/MultipleUsers/MultipleUsers";

import styles from "./HomePage.module.scss";

const isSelfLookup = (customerPhone, cashierPhone) => {
  if (!customerPhone || !cashierPhone) {
    return false;
  }

  return customerPhone.replace(/\D/g, "") === cashierPhone.replace(/\D/g, "");
};

const handleClickViewCustomerOrders = (
  phoneNumber,
  isdCode,
  countryId,
  countryCode,
  navigate,
  openSnackbar,
  selectedBusinessVertical,
  fetchCustomerInfo,
  setShowMultipleUsersDialog,
  setMultipleUsersList
) => {
  fetchCustomerInfo(
    {
      phoneNumber,
      isdCode,
      countryId,
      countryCode,
      selectedBusinessVertical,
    },
    {
      onSuccess: (customerDetails) => {
        if (!Array.isArray(customerDetails)) {
          openSnackbar(ERROR_MESSAGES.UNEXPECTED_RESPONSE, "error");
          return;
        }
        if (customerDetails.length > 1) {
          setShowMultipleUsersDialog(true);
          setMultipleUsersList(customerDetails);
          return;
        }
        if (customerDetails.length === 1) {
          const customerId = customerDetails[0].uamId;
          navigate(
            buildCustomerOrdersPath({ customerId, countryId, isdCode }),
          );
          return;
        }
        openSnackbar(ERROR_MESSAGES.CUSTOMER_NOT_FOUND, "error");
      },
      onError: (error) => {
        openSnackbar(
          error.message || ERROR_MESSAGES.CUSTOMER_FETCH_FAILED,
          "error"
        );
      },
    }
  );
};

const handleChangeBusinessVertical = (event, setSelectedBusinessVertical) => {
  const selectedBusinessVerticalId = event.target.value;
  setSelectedBusinessVertical(selectedBusinessVerticalId);
};

const canViewCustomerOrders = (
  phoneNumber,
  isdCode,
  selectedBusinessVertical,
  cashierPhoneNumber,
  verticalMenuItems
) => {
  if (isSelfLookup(phoneNumber, cashierPhoneNumber)) {
    return false;
  }
  const allowedVerticalValues = verticalMenuItems.map((item) =>
    String(item.value),
  );
  return (
    Boolean(isdCode) &&
    isValidPhoneForIsd(phoneNumber, isdCode) &&
    allowedVerticalValues.includes(String(selectedBusinessVertical))
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const { data: countries = [] } = useCountriesMappings();
  const [cashierPhoneNumber] = useState(
    localStorage.getItem("cashierPhoneNumber")
  );
  const [showMultipleUsersDialog, setShowMultipleUsersDialog] = useState(false);
  const [multipleUsersList, setMultipleUsersList] = useState([]);

  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState(null);

  const { mutate: fetchCustomerInfo, isPending: isCustomerInfoLoading } =
    useFetchCustomerInfo();

  const resolvedCountry = useMemo(() => {
    if (!countries.length) {
      return null;
    }
    if (countryCode) {
      return (
        countries.find((country) => country.countryCode === countryCode) ||
        null
      );
    }
    return getDefaultCountry(countries);
  }, [countries, countryCode]);

  const isdCode = resolvedCountry?.isdCode;
  const countryId = resolvedCountry?.countryId;
  const learnOnly = isLearnOnlyRegion({
    countryId,
    countryCode: resolvedCountry?.countryCode,
    isdCode,
  });

  const verticalMenuItems = useMemo(() => {
    if (learnOnly) {
      return BUSINESS_VERTICAL_IDS.filter(
        (vertical) => Number(vertical.id) === Number(LEARN_VERTICAL_ID),
      );
    }
    return BUSINESS_VERTICAL_IDS;
  }, [learnOnly]);

  useEffect(() => {
    if (!learnOnly || selectedBusinessVertical == null) {
      return;
    }
    if (Number(selectedBusinessVertical) !== Number(LEARN_VERTICAL_ID)) {
      setSelectedBusinessVertical(String(LEARN_VERTICAL_ID));
    }
  }, [learnOnly, selectedBusinessVertical]);

  if (isCustomerInfoLoading) {
    return <Loader />;
  }

  const handleViewCustomerOrders = () => {
    if (isSelfLookup(phoneNumber, cashierPhoneNumber)) {
      openSnackbar(ERROR_MESSAGES.SELF_LOOKUP_BLOCKED, "error");
      return;
    }
    handleClickViewCustomerOrders(
      phoneNumber,
      isdCode,
      countryId,
      resolvedCountry?.countryCode,
      navigate,
      openSnackbar,
      selectedBusinessVertical,
      fetchCustomerInfo,
      setShowMultipleUsersDialog,
      setMultipleUsersList
    );
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.searchCustomerContainer}>
        <div className={styles.inputContainer}>
          <Text>Please enter customer's registered mobile number.</Text>
          <PhoneWithIsdInput
            phone={phoneNumber}
            onPhoneChange={setPhoneNumber}
            countryCode={countryCode || resolvedCountry?.countryCode || ""}
            onCountryCodeChange={setCountryCode}
            phoneLabel="Enter a Phone number"
          />
        </div>
        <div className={styles.businessVertical}>
          <Text>Please select the business vertical</Text>
          <SingleSelect
            tag="Select business vertical"
            value={selectedBusinessVertical}
            menuItems={verticalMenuItems}
            handleChange={(event) =>
              handleChangeBusinessVertical(event, setSelectedBusinessVertical)
            }
          />
        </div>
        <div className={styles.viewCustomerDetailsButton}>
          <PrimaryButton
            onClick={handleViewCustomerOrders}
            disabled={
              !canViewCustomerOrders(
                phoneNumber,
                isdCode,
                selectedBusinessVertical,
                cashierPhoneNumber,
                verticalMenuItems
              )
            }
          >
            {isCustomerInfoLoading ? <Loader /> : "View Customer Details"}
          </PrimaryButton>
        </div>
      </div>
      <Dialog
        open={showMultipleUsersDialog}
        handleClose={() => setShowMultipleUsersDialog(false)}
      >
        <MultipleUsers
          customerDetails={multipleUsersList}
          handleOnSelectUser={(uamId) => {
            setShowMultipleUsersDialog(false);
            navigate(
              buildCustomerOrdersPath({
                customerId: uamId,
                countryId,
                isdCode,
              }),
            );
          }}
        />
      </Dialog>
    </div>
  );
};

export default HomePage;
